import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import Modal from "@via/components/modal";
import {
  SpreadsheetData,
  SpreadsheetEditor,
} from "@via/components/spreadsheet-editor/spreadsheet-editor";
import { useEditCustomBoundary } from "@via/pages/account/admin/admin.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { ModalSize } from "@via/utils/constants";
import { formatSpreadsheetCellValue } from "@via/pages/data/premium-dataset/utils/helper";
import { ListCustomerBoundaryResponseBodyItem } from "common";
import { useEffect, useState } from "react";
import { NOTIFICATION } from "@via/context/notifications-context";
import { ErrorNotification } from "@via/utils/interfaces";

interface EditCustomerBoundaryProps {
  customerBoundaryFiles: ListCustomerBoundaryResponseBodyItem[];
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
}

// Every visible column is sent back on Update, and the update schema refuses keys it does not know,
// so anything the list returns that is not a boundary field has to be hidden here.
const HIDE_COLUMNS = [
  "id",
  "revokedAt",
  "claimedAt",
  "organizationCustomerBoundaryId",
  "claimedOrganizationName",
  "claimedOrganizationId",
  "claimedUserId",
  "claimedUserEmail",
];

const EditCustomerBoundary = ({
  customerBoundaryFiles,
  isLoading,
  isOpen,
  onClose,
}: EditCustomerBoundaryProps) => {
  const { setNotification } = useNotifications();
  const [labels, setLabels] = useState<string[]>([]);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [errors, setErrors] = useState([]);
  const [changedRows, setChangedRows] = useState<Set<number>>(new Set());

  const {
    mutateAsync: editCustomBoundary,
    isPending: isEditCustomBoundaryLoading,
  } = useEditCustomBoundary();

  useEffect(() => {
    if (customerBoundaryFiles?.length) {
      const customerBoundaryFile = customerBoundaryFiles[0];
      const allLabels = Object.keys(customerBoundaryFile);
      const filteredLabels = allLabels.filter(
        label => !HIDE_COLUMNS.includes(label)
      );
      setLabels(filteredLabels);

      const allSpreadsheetData = customerBoundaryFiles.map(file => {
        return (
          filteredLabels?.map(label => {
            const value = file[label];
            return formatSpreadsheetCellValue(value);
          }) || []
        );
      });

      setLabels(filteredLabels);
      setSpreadsheetData(allSpreadsheetData);
    }
  }, [customerBoundaryFiles]);

  const updateSpreadsheetData = (
    data: SpreadsheetData,
    rowIndex?: number,
    rowIndices?: number[]
  ) => {
    setSpreadsheetData(data);
    const changedRowIndices =
      rowIndices && rowIndices.length > 0
        ? rowIndices
        : rowIndex !== undefined
          ? [rowIndex]
          : [];
    if (changedRowIndices.length === 0) {
      return;
    }
    setChangedRows(prev => new Set([...prev, ...changedRowIndices]));
  };

  const handleConfirm = async () => {
    const transformedDatas: any = [];
    const rowErrors: string[] = [];
    try {
      // Only process rows that have been modified
      const changedRowsArray = Array.from(changedRows);
      changedRowsArray.forEach(rowIndex => {
        const row = spreadsheetData[rowIndex];
        const transformedData: any = {};

        row.forEach((cell, colIndex) => {
          const label = labels[colIndex];
          if (label) {
            const value = cell.value?.toString() || "";
            transformedData[label] =
              value.startsWith("{") || value.startsWith("[")
                ? JSON.parse(value)
                : value;
          }
        });
        transformedData["id"] = customerBoundaryFiles[rowIndex]?.id || "";
        transformedDatas.push(transformedData);
      });
    } catch (_error) {
      setNotification({
        id: "error-transforming-data",
        message: "Error transforming data",
        details: "Please ensure the data is in the correct format.",
        variant: NOTIFICATION.ERROR,
      });
      return;
    }

    try {
      for (let i = 0; i < transformedDatas.length; i++) {
        await editCustomBoundary(transformedDatas[i]);
      }
      setChangedRows(new Set()); // Clear changed rows after successful update
    } catch (err: ErrorNotification | any) {
      if (err?.response?.data?.details) {
        const errors = err.response.data.details.reduce((acc, error) => {
          const errorMessage = `${error.path?.[0] ?? "unknown field"}: ${error.message ?? "unknown error"}`;
          acc.push(errorMessage);
          return acc;
        }, []);

        setErrors(errors);
        rowErrors.push(...errors);
      }
    }

    if (rowErrors?.length === 0) {
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Edit Customer Boundary"
      showButtons={false}
      isLoading={isLoading}
      size={ModalSize.Large}>
      {customerBoundaryFiles?.length ? (
        <>
          <MessageBanner messages={errors} type={BannerType.Error} />
          <div className="mb-5">
            <SpreadsheetEditor
              columnLabels={labels}
              data={spreadsheetData}
              onChange={updateSpreadsheetData}
              isLoading={isEditCustomBoundaryLoading}
              disableContextMenu
            />
            <div className="flex justify-end mt-4">
              <button
                id="update-button"
                type="button"
                disabled={changedRows.size === 0 || isEditCustomBoundaryLoading}
                onClick={e => {
                  e.preventDefault();
                  handleConfirm();
                }}
                className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Update
              </button>
              <button
                id="cancel-button"
                type="button"
                className="ml-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </>
      ) : null}
    </Modal>
  );
};

export default EditCustomerBoundary;
